import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Heading } from "@/components/heading";
import { Button } from "@/components/button";
import { ArrowLeftIcon, EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import PageContainer from "@/components/layouts/PageContainer";
import { Fieldset, Legend } from "@/components/fieldset";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  DropdownDivider,
} from "@/components/dropdown";
import { useScoreConversionTableQuery, useDeleteScoreConversionTableMutation } from "@/apis/score_conversion_tables";

const ScoreConversionTableDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  const { data: scoreTable, isLoading, error } = useScoreConversionTableQuery(id || "");
  const { mutate: deleteScoreTable, isPending: isDeleting } = useDeleteScoreConversionTableMutation();

  // Handle delete
  const handleDelete = () => {
    if (!scoreTable || isDeleting || !id) return;

    if (
      window.confirm("Are you sure you want to delete this score conversion table?")
    ) {
      setDeleteError(null);

      deleteScoreTable(id, {
        onSuccess: () => {
          // Navigate back to previous page
          navigate(-1);
        },
        onError: (error: any) => {
          // Show error message
          setDeleteError(error?.message || "Failed to delete score conversion table");
        },
      });
    }
  };

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center py-8 text-red-500">
          Invalid score conversion table ID.
        </div>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="text-center py-8">Loading score conversion table...</div>
      </PageContainer>
    );
  }

  if (error || !scoreTable) {
    return (
      <PageContainer>
        <div className="text-center py-8 text-red-500">
          Error loading score conversion table. Please try again.
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {deleteError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {deleteError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={() => navigate(-1)}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>
            <div>
              <Heading>Score Conversion Table</Heading>
              <p className="text-gray-600">
                View score conversion mapping for scale scores 1-36.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => navigate(`/score_conversion_tables/${id}/edit`)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Edit
            </Button>
            
            <Dropdown>
              <DropdownButton color="white" aria-label="More options">
                <EllipsisVerticalIcon className="h-4 w-4" />
              </DropdownButton>

              <DropdownMenu>
                <DropdownItem
                  danger
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        <Fieldset>
          <Legend>Score Conversion Mapping</Legend>
          <div className="mt-4">
            {/* Table Container */}
            <div className="max-w-xs">
              {/* Table Header */}
              <div className="grid gap-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-t-md font-medium text-gray-700 text-sm" style={{gridTemplateColumns: "60px 1fr"}}>
                <div className="flex items-center justify-center">Scale</div>
                <div>Raw Score</div>
              </div>
              
              {/* Table Rows */}
              <div className="border-l border-r border-b border-gray-200 rounded-b-md">
                {Array.from({ length: 36 }, (_, index) => {
                  const scaleScore = 36 - index; // 36부터 1까지 내림차순
                  const mapping = scoreTable.scale_to_raw_mapping.find(
                    m => m.scale_score === scaleScore
                  );
                  
                  let rawScoreDisplay = "-";
                  if (mapping?.raw_score) {
                    if (mapping.raw_score.min === mapping.raw_score.max) {
                      rawScoreDisplay = mapping.raw_score.min.toString();
                    } else {
                      rawScoreDisplay = `${mapping.raw_score.min}-${mapping.raw_score.max}`;
                    }
                  }
                  
                  return (
                    <div key={scaleScore} className="grid gap-3 px-3 py-1.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50" style={{gridTemplateColumns: "60px 1fr"}}>
                      <div className="flex items-center justify-center font-medium text-gray-900 text-sm">
                        {scaleScore}
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        {rawScoreDisplay}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Fieldset>
      </div>
    </PageContainer>
  );
};

export default ScoreConversionTableDetailPage; 